// src/lib/stripe.ts: Stripe.js for the payment form in Settings (docs/SPEC.md 16.6; docs/DECISIONS.md "Billing"). The
// script comes from https://js.stripe.com/v3 (PCI requires Stripe's own domain; never bundled, never self-hosted) and is
// loaded once, on demand, only when a payment form opens. The CSP allows js.stripe.com for script-src and frame-src,
// hooks.stripe.com for frame-src (card checks) and api.stripe.com for connect-src (public/_headers).
//
// Only the few calls the form makes are typed here, so no Stripe package ships in the app.
// appearance() styles Stripe's fields as ours: Onest (our own files, which /fonts/* lets js.stripe.com read), the 8px
// control radius, the control border, the focus ring in the builder's accent, and our danger color.

export const STRIPE_JS = "https://js.stripe.com/v3/";

export type StripeAddress = { line1: string; line2: string | null; city: string; state: string; postal_code: string; country: string };
export type StripeError = { type: string; code?: string; decline_code?: string; message?: string };
export type StripeSetupIntent = { id: string; status: string; next_action?: { type: string } | null };
export type StripeElementChange = { complete: boolean; empty?: boolean; value?: { type?: string; name?: string; address?: StripeAddress } };

export type StripeElement = {
  mount(el: HTMLElement): void;
  destroy(): void;
  on(event: "ready" | "loaderror" | "change", handler: (e: StripeElementChange & { error?: StripeError }) => void): void;
};
export type StripeElements = {
  create(type: "payment", options: Record<string, unknown>): StripeElement;
  create(type: "address", options: Record<string, unknown>): StripeElement;
  submit(): Promise<{ error?: StripeError }>;
};
/** A bank account Stripe's bank window collected, on the SetupIntent it answers (expand: ["payment_method"]). */
export type StripeBankSetupIntent = StripeSetupIntent & {
  payment_method?: string | { us_bank_account?: { bank_name?: string | null; last4?: string | null } | null } | null;
};
export type StripeInstance = {
  /** With the SetupIntent's secret, or deferred (mode "setup"): the card fields alone, the secret given at confirm. */
  elements(
    options: ({ clientSecret: string } | { mode: "setup"; currency: string; paymentMethodTypes: string[] }) & {
      appearance: Appearance; fonts?: FontSource[]; loader?: "auto" | "always" | "never";
    },
  ): StripeElements;
  confirmSetup(options: {
    elements: StripeElements;
    /** Deferred elements: the SetupIntent to confirm. */
    clientSecret?: string;
    redirect: "if_required";
    confirmParams: {
      return_url: string;
      /** The billing address, passed here because the payment fields leave it to the form (fields.billingDetails.address "never"). */
      payment_method_data?: { billing_details: { address: StripeAddress; name?: string; email?: string } };
    };
  }): Promise<{ error?: StripeError; setupIntent?: StripeSetupIntent }>;
  /** Stripe's bank window (Financial Connections): search, the bank's own sign-in, or the account numbers by hand. */
  collectBankAccountForSetup(options: {
    clientSecret: string;
    params: {
      payment_method_type: "us_bank_account";
      payment_method_data: { billing_details: { name: string; email?: string; address?: StripeAddress } };
    };
    expand?: string[];
  }): Promise<{ error?: StripeError; setupIntent?: StripeBankSetupIntent }>;
  /** Confirms the SetupIntent with the bank account collected, once the debit authorization has been shown. */
  confirmUsBankAccountSetup(clientSecret: string): Promise<{ error?: StripeError; setupIntent?: StripeSetupIntent }>;
};
type StripeFactory = (publishableKey: string) => StripeInstance;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

let loading: Promise<StripeFactory> | null = null;
const instances = new Map<string, StripeInstance>();

/** Loads Stripe.js once and returns the client for this key. Rejects when it cannot load (offline, blocked). */
export async function loadStripe(publishableKey: string): Promise<StripeInstance> {
  const cached = instances.get(publishableKey);
  if (cached) return cached;
  loading ??= new Promise<StripeFactory>((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }
    const script = document.createElement("script");
    script.src = STRIPE_JS;
    script.async = true;
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe);
      else {
        loading = null;
        reject(new Error("stripe_unavailable"));
      }
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("stripe_unavailable"));
    };
    document.head.appendChild(script);
  });
  const factory = await loading;
  const stripe = factory(publishableKey);
  instances.set(publishableKey, stripe);
  return stripe;
}

/** Forgets the loaded script and clients (tests). */
export function forgetStripe(): void {
  loading = null;
  instances.clear();
}

export type Appearance = { theme: "stripe"; labels: "above"; variables: Record<string, string>; rules: Record<string, Record<string, string>> };
export type FontSource = { family: string; src: string; weight: string; style: string; display: string; unicodeRange?: string };

/** A token's value on <html> (theme.ts sets the accent's), else the fallback. */
function token(name: string, fallback: string): string {
  const v = typeof getComputedStyle === "function" ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() : "";
  return v || fallback;
}

/** Onest's two files (tokens.css), at 400, 500 and 600, by absolute URL: they load inside Stripe's frames. */
export function fonts(origin: string = location.origin): FontSource[] {
  const latin = "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";
  const ext = "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF";
  return ["400", "500", "600"].flatMap((weight) => [
    { family: "Onest", src: `url(${origin}/fonts/onest-latin-wght-normal.woff2)`, weight, style: "normal", display: "swap", unicodeRange: latin },
    { family: "Onest", src: `url(${origin}/fonts/onest-latin-ext-wght-normal.woff2)`, weight, style: "normal", display: "swap", unicodeRange: ext },
  ]);
}

/**
 * Stripe's Appearance API set to design system A (docs/design/system/tokens-a.md): Onest, ink and greys for text, the
 * 8px control radius and 10px tab radius, the control border and its soft shadow, the focus ring in the builder's
 * accent (its strong shade and tint-2 halo), and #A3362A for errors. 16px fields on phones (iOS zooms anything smaller).
 */
export function appearance(phone: boolean): Appearance {
  const ink = token("--hb-900", "#1A1917");
  const text2 = token("--hb-600", "#5A5853");
  const text3 = token("--hb-500", "#6C6964");
  const line = token("--hb-200", "#E4E3E0");
  const line2 = token("--hb-300", "#D2D1CD");
  const line3 = token("--hb-400", "#A6A4A0");
  const hover = token("--hb-75", "#FAFAF9");
  const placeholder = token("--color-placeholder", "#8C8983");
  const danger = token("--color-danger", "#A3362A");
  const accent = token("--brand-accent", ink);
  const strong = token("--brand-accent-strong", ink);
  const tint = token("--brand-accent-tint", "#F4F3F3");
  const tint2 = token("--brand-accent-tint-2", "#E7E6E5");
  const size = phone ? "16px" : "15px";
  const ring = `0 0 0 3px ${tint2}`;
  return {
    theme: "stripe",
    labels: "above",
    variables: {
      fontFamily: `Onest, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`,
      fontSizeBase: size,
      fontSizeSm: "13px",
      fontWeightNormal: "400",
      fontWeightMedium: "500",
      fontWeightBold: "600",
      fontLineHeight: "1.5",
      colorPrimary: strong,
      colorBackground: "#FFFFFF",
      colorText: ink,
      colorTextSecondary: text2,
      colorTextPlaceholder: placeholder,
      colorDanger: danger,
      colorIcon: text3,
      colorIconTabSelected: strong,
      borderRadius: "8px",
      spacingUnit: "4px",
      gridRowSpacing: "18px",
      gridColumnSpacing: "12px",
      tabSpacing: "10px",
      focusBoxShadow: ring,
      focusOutline: "none",
    },
    rules: {
      ".Label": { fontSize: "14px", fontWeight: "500", color: ink, marginBottom: "6px", letterSpacing: "-0.006em" },
      ".Input": { border: `1px solid ${line2}`, boxShadow: "0 1px 2px rgba(26, 25, 23, .06)", padding: "11px 14px", fontSize: size, color: ink },
      ".Input:hover": { borderColor: line3 },
      ".Input:focus": { border: `1px solid ${strong}`, boxShadow: ring, outline: "none" },
      ".Input--invalid": { border: `1px solid ${danger}`, boxShadow: "none", color: ink },
      ".Input::placeholder": { color: placeholder },
      ".Error": { fontSize: "14px", color: danger, marginTop: "6px" },
      ".Tab": { border: `1px solid ${line}`, borderRadius: "10px", boxShadow: "none", padding: "12px 14px", backgroundColor: "#FFFFFF" },
      ".Tab:hover": { borderColor: line3, backgroundColor: hover, color: ink },
      ".Tab:focus": { boxShadow: ring, outline: "none" },
      ".Tab--selected": { borderColor: accent, backgroundColor: tint, boxShadow: `inset 0 0 0 1px ${accent}`, color: ink },
      ".Tab--selected:hover": { borderColor: accent, backgroundColor: tint, color: ink },
      ".Tab--selected:focus": { borderColor: accent, boxShadow: `inset 0 0 0 1px ${accent}, ${ring}` },
      ".TabLabel": { fontWeight: "500" },
      ".TabIcon--selected": { fill: strong },
      ".Block": { border: `1px solid ${line}`, borderRadius: "12px", boxShadow: "none" },
      ".PickerItem": { border: `1px solid ${line}`, borderRadius: "10px", boxShadow: "none" },
      ".PickerItem--selected": { borderColor: accent, boxShadow: `inset 0 0 0 1px ${accent}` },
      ".TermsText": { fontSize: "13px", lineHeight: "1.5", color: text3 },
      ".Link": { color: strong },
    },
  };
}
